import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext.js';
import { fetchSubmissions } from '../../api/portal-api.js';
import type { SubmissionListItem, SubmissionListResponse } from '@qdf/shared';

import { SandboxBanner, SANDBOX_BANNER_HEIGHT, isSandboxEnv } from '../layout/SandboxBanner.js';

interface NewSubmissionToast {
  item: SubmissionListItem;
  receivedAt: Date;
}

export function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [toast, setToast] = useState<NewSubmissionToast | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  const showBanner = isSandboxEnv();
  const bannerOffset = showBanner ? SANDBOX_BANNER_HEIGHT : 0;

  // Poll for submissions — detect new ones
  const { data } = useQuery<SubmissionListResponse>({
    queryKey: ['portal-submissions-all'],
    queryFn: () => fetchSubmissions({ limit: 500 }),
    refetchInterval: 30_000, // Poll every 30s
  });

  const handleNewSubmission = useCallback((item: SubmissionListItem) => {
    setToast({ item, receivedAt: new Date() });

    // Auto-dismiss after 30 seconds
    setTimeout(() => {
      setToast(prev => prev?.item.submission_id === item.submission_id ? null : prev);
    }, 30_000);

    // Browser notification if supported and permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('New QDF Submission', {
        body: `${item.submission_type === 'form' ? 'Form' : 'API'}: ${item.full_name || item.property_address_1 || item.submission_id}`,
        icon: '⚡',
      });
    }
  }, []);

  useEffect(() => {
    if (!data?.items) return;

    const currentIds = new Set(data.items.map(i => i.submission_id));

    if (isInitialLoadRef.current) {
      // First load — seed known IDs, don't alert
      knownIdsRef.current = currentIds;
      isInitialLoadRef.current = false;
    } else {
      // Compare — find new submissions
      for (const item of data.items) {
        if (!knownIdsRef.current.has(item.submission_id)) {
          handleNewSubmission(item);
          break; // Show one at a time
        }
      }
      knownIdsRef.current = currentIds;
    }

    // Count pending (new status) items
    const pending = data.items.filter(i => i.status === 'new').length;
    setPendingCount(pending);
  }, [data, handleNewSubmission]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const dismissToast = () => setToast(null);

  const reviewNow = () => {
    if (toast) {
      navigate(`/portal/submissions/${toast.item.submission_id}`);
      setToast(null);
    }
  };

  return (
    <>
      <SandboxBanner />
      <div style={styles.wrapper}>
      {/* Sidebar */}
      <nav style={{ ...styles.sidebar, top: `${bannerOffset}px`, height: `calc(100vh - ${bannerOffset}px)` }}>
        {/* Logo */}
        <div style={styles.logoSection}>
          <div style={styles.logoIcon}>⚡</div>
          <div>
            <div style={styles.logoText}>QDF Portal</div>
            <div style={styles.logoSub}>Operations Center</div>
          </div>
        </div>

        {/* Nav Links */}
        <div style={styles.navLinks}>
          <div style={styles.navLabel}>MAIN</div>
          <SidebarLink to="/portal" end icon="📊" label="Dashboard" />
          <SidebarLink to="/portal/submissions" icon="📋" label="Submissions" badge={pendingCount} />
          <SidebarLink to="/portal/reports" icon="📈" label="Monthly Reports" />
          <SidebarLink to="/portal/email-templates" icon="✉️" label="Email Templates" />
        </div>

        {/* SLA Indicator */}
        <div style={styles.slaSection}>
          <div style={styles.slaLabel}>Target SLA</div>
          <div style={styles.slaValue}>30 min</div>
          <div style={styles.slaSub}>Review all new submissions within 30 minutes</div>
        </div>

        {/* User */}
        <div style={styles.userSection}>
          {user?.picture ? (
            <img src={user.picture} alt="" style={styles.avatar} referrerPolicy="no-referrer" />
          ) : (
            <div style={styles.avatarPlaceholder}>
              {user?.name?.charAt(0) || '?'}
            </div>
          )}
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user?.name}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
          <button onClick={logout} style={styles.logoutBtn} title="Sign out">
            ↪
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        {/* New Submission Toast */}
        {toast && (
          <div style={{ ...styles.toastOverlay, top: `${20 + bannerOffset}px` }}>
            <div style={styles.toast}>
              <div style={styles.toastPulse} />
              <div style={styles.toastContent}>
                <div style={styles.toastHeader}>
                  <span style={styles.toastIcon}>🔔</span>
                  <strong style={styles.toastTitle}>New Submission Received!</strong>
                  <span style={styles.toastTime}>
                    {toast.receivedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={styles.toastBody}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, marginRight: '8px',
                    background: toast.item.submission_type === 'form' ? '#eef2ff' : '#f5f3ff',
                    color: toast.item.submission_type === 'form' ? '#4338ca' : '#7c3aed',
                  }}>
                    {toast.item.submission_type === 'form' ? 'Form' : 'API'}
                  </span>
                  <span style={{ color: '#1e293b', fontWeight: 500 }}>
                    {toast.item.full_name || toast.item.property_address_1 || toast.item.submission_id}
                  </span>
                  {(toast.item.loc_amount_requested || toast.item.fund_amount_requested) && (
                    <span style={{ color: '#059669', fontWeight: 600, marginLeft: '8px' }}>
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(
                        toast.item.loc_amount_requested || toast.item.fund_amount_requested || 0
                      )}
                    </span>
                  )}
                </div>
                <div style={styles.toastActions}>
                  <button onClick={reviewNow} style={styles.toastReviewBtn}>
                    Review Now →
                  </button>
                  <button onClick={dismissToast} style={styles.toastDismissBtn}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <Outlet />
      </main>
      </div>
    </>
  );
}

function SidebarLink({ to, icon, label, end, badge }: { to: string; icon: string; label: string; end?: boolean; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        ...styles.navLink,
        background: isActive ? '#eef2ff' : 'transparent',
        color: isActive ? '#4338ca' : '#475569',
        fontWeight: isActive ? 600 : 500,
        borderLeft: isActive ? '3px solid #4338ca' : '3px solid transparent',
      })}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span style={styles.badge}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  sidebar: {
    width: '260px',
    background: '#ffffff',
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'fixed' as const,
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 10,
    boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 20px',
    borderBottom: '1px solid #e2e8f0',
  },
  logoIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #4338ca, #6366f1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  logoText: {
    color: '#1e293b',
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
  },
  logoSub: {
    color: '#94a3b8',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.3px',
  },
  navLinks: {
    flex: 1,
    padding: '16px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  navLabel: {
    color: '#94a3b8',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    padding: '0 16px 8px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'all 0.15s',
  },
  badge: {
    background: '#ef4444',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    minWidth: '22px',
    height: '22px',
    borderRadius: '11px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
    animation: 'pulse 2s ease-in-out infinite',
  },
  slaSection: {
    margin: '0 16px 16px',
    padding: '14px 16px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '10px',
  },
  slaLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#059669',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  slaValue: {
    fontSize: '22px',
    fontWeight: 800,
    color: '#047857',
    letterSpacing: '-0.5px',
  },
  slaSub: {
    fontSize: '11px',
    color: '#64748b',
    lineHeight: '1.4',
    marginTop: '2px',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    background: '#fafbfc',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#eef2ff',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    color: '#1e293b',
    fontSize: '13px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  userEmail: {
    color: '#94a3b8',
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  logoutBtn: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  main: {
    flex: 1,
    marginLeft: '260px',
    minHeight: '100vh',
    overflow: 'auto',
    position: 'relative' as const,
  },
  // ── Toast ──────────────────────────────────────────────────────
  toastOverlay: {
    position: 'fixed' as const,
    top: '20px',
    left: '300px',
    right: '20px',
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none' as const,
  },
  toast: {
    maxWidth: '640px',
    width: '100%',
    background: '#ffffff',
    border: '2px solid #4338ca',
    borderRadius: '14px',
    boxShadow: '0 12px 40px rgba(67, 56, 202, 0.15), 0 4px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    pointerEvents: 'auto' as const,
    animation: 'slideDown 0.3s ease-out',
  },
  toastPulse: {
    height: '4px',
    background: 'linear-gradient(90deg, #4338ca, #6366f1, #818cf8, #6366f1, #4338ca)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 2s ease-in-out infinite',
  },
  toastContent: {
    padding: '16px 20px',
  },
  toastHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  toastIcon: {
    fontSize: '20px',
  },
  toastTitle: {
    fontSize: '15px',
    color: '#1e293b',
    flex: 1,
  },
  toastTime: {
    fontSize: '12px',
    color: '#94a3b8',
    fontWeight: 500,
  },
  toastBody: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '14px',
    fontSize: '14px',
  },
  toastActions: {
    display: 'flex',
    gap: '8px',
  },
  toastReviewBtn: {
    flex: 1,
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    background: '#4338ca',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  toastDismissBtn: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
